import { ComponentPropsWithoutRef } from "react";
import cx from "classnames";

import { useDTFocusDispatchContext } from "../DataTable/internal/context/focus/useDTFocusDispatchContext";

import styles from "./TableBase.module.scss";

/**
 * Props for the SimpleTableBase component
 * @extends ComponentPropsWithoutRef<"table">
 */
export type SimpleTableBaseProps = ComponentPropsWithoutRef<"table">;

/**
 * Props for the DataTableBase component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "aria-colcount" | "aria-rowcount">
 */
export type DataTableBaseProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "aria-colcount" | "aria-rowcount"
> & {
  /**
   * The number of columns in the table
   */
  colCount: number;
  /**
   * The number of rows in the table
   */
  rowCount: number;
  /**
   * Whether the table has expandable rows
   */
  hasSubRows?: boolean;
};

/**
 * Props for the TableBase component
 * @extends SimpleTableBaseProps
 * @extends DataTableBaseProps
 */
export type TableBaseProps =
  | ({
      type: "simple";
    } & SimpleTableBaseProps)
  | ({
      type: "data-table";
    } & DataTableBaseProps);

/**
 * Base table component for simple presentational tables
 * @param props - The props for the SimpleTableBase component
 * @param props.children - The children of the SimpleTableBase component
 * @param props.className - The class name of the SimpleTableBase component
 * @param props.ref - The ref of the SimpleTableBase component
 */
const SimpleTableBase = ({
  children,
  className,
  ...rest
}: SimpleTableBaseProps) => {
  const tableBaseClassNames = cx(className, styles["table-base"]);
  return (
    <table
      data-anv="simple-table-base"
      {...rest}
      className={tableBaseClassNames}
    >
      {children}
    </table>
  );
};

/**
 * Base table component for complex data tables
 * @param props - The props for the DataTableBase component
 * @param props.children - The children of the DataTableBase component
 * @param props.className - The class name of the DataTableBase component
 * @param props.ref - The ref of the DataTableBase component
 */
const DataTableBase = ({
  children,
  className,
  colCount,
  rowCount,
  hasSubRows,
  ...rest
}: DataTableBaseProps) => {
  const tableBaseClassNames = cx(className, styles["data-table-base"]);
  const { focusTableGenerally, blur } = useDTFocusDispatchContext();

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      blur();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      focusTableGenerally();
    }
  };

  return (
    <div
      data-anv="data-table-base"
      {...rest}
      className={tableBaseClassNames}
      aria-colcount={colCount}
      aria-rowcount={rowCount}
      role={hasSubRows ? "treegrid" : "grid"}
      tabIndex={0}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {children}
    </div>
  );
};

/**
 * Styled table component for simple presentational or complex data tables
 * @param props - The props for the TableBase component
 * @param props.type - The type of the TableBase component
 * @param props.children - The children of the TableBase component
 * @param props.className - The class name of the TableBase component
 * @param props.ref - The ref of the TableBase component
 */
export const TableBase = ({ type, ...rest }: TableBaseProps) => {
  if (type === "simple") {
    return <SimpleTableBase {...rest} />;
  }

  if ("rowCount" in rest && "colCount" in rest) {
    return (
      <DataTableBase
        {...rest}
        rowCount={rest.rowCount}
        colCount={rest.colCount}
      />
    );
  }
};
